/**
 * VozidlaList — Phase 2.1 list workspace.
 *
 * Source: .agents/design/project/artboard-radical-vozidla.jsx
 *
 * Three-column shell: rail · list · inspector preview. Selecting a row updates
 * the inspector preview (the full detail page is Phase 2.2). Filters re-fetch
 * via TanStack Query; list status (Vše / Skladová / Registrovaná / Vyplacená)
 * and a brand chip are wired today, the rest of the toolband is layout for now.
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { Badge } from '@/components/Badge';
import { BatteryDot } from '@/components/BatteryDot';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { ColorSwatch, vehicleColorToHex } from '@/components/ColorSwatch';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DataTable, type DataTableColumn, type RowTint } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Inspector, FieldRow } from '@/components/Inspector';
import { LoadMoreBar } from '@/components/LoadMoreBar';
import { Kbd } from '@/components/Kbd';
import { Menu } from '@/components/Menu';
import { Pill } from '@/components/Pill';
import { ReservationFlag } from './ReservationFlag';
import { SearchInput } from '@/components/SearchInput';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { Subtabs } from '@/components/Subtabs';
import { Toolband } from '@/components/Toolband';
import { formatDate, formatDecimal, formatInt } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import { RozsahChip } from '@/modules/skupiny/RozsahChip';
import { otherFirmaName, useActiveFirmaId } from '@/modules/skupiny/otherFirma';
import { useRozsah } from '@/modules/skupiny/rozsah';
import {
  useBrandsQuery,
  useVehicleModelsQuery,
  useVehiclesInfiniteQuery,
  type Vehicle,
} from './api';
import {
  DEFAULT_STAV,
  STAV_LABEL,
  type StavFilter,
  batteryLevel,
  deliveryMonthLabel,
  deliveryMonthOptions,
  isRegistrovany,
  isVyplaceny,
  stavBadgeLabel,
  stavBadgeTone,
  vehicleDisplayName,
  vozidlaFiltersFromSearch,
} from './helpers';

type Subtab = 'vozidla' | 'upravena' | 'nabidky' | 'smlouvy' | 'vyhodnoceni' | 'sprava';

// Text-bearing columns share the spare width instead of a single flex track —
// a lone minmax(0,1fr) provedení used to absorb all slack while umístění
// stayed clamped at 100px and clipped. Model caps at 180px (model names are a
// bounded catalog; each row is its own grid, so max-content can't align rows).
const COLUMNS_TEMPLATE =
  '28px 100px 80px minmax(110px,180px) minmax(150px,1.5fr) 110px minmax(100px,1fr) 36px minmax(124px,170px)';

export function VozidlaList() {
  const navigate = useNavigate();
  const urlSearch = useSearch({ from: '/vozidla' });
  // Default is `Skladový`, as in the legacy list. The API expands `Skladový` to BOTH `Skladový`
  // and `Registrovaný` rows (vehicle.repository.ts:73-77) — that's the
  // legacy "in-stock + registered for delivery" view.
  const stav: StavFilter = urlSearch.stav ?? DEFAULT_STAV;
  const brandId: number | null = urlSearch.brand ?? null;
  const modelId: number | null = urlSearch.model ?? null;
  const deliveryMonth: string | null = urlSearch.deliveryMonth ?? null;
  const monthOptions = useMemo(() => deliveryMonthOptions(), []);
  const { isPhone } = useBreakpoint();
  const { can } = useModuleAccess();
  const canWrite = can('vozidla', 'write');
  const canSprava = can('vozidla', 'admin');
  const [subtab, setSubtab] = useState<Subtab>('vozidla');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Local state for the search input — immediate caret response, then a
  // debounce pushes it to the URL so the detail page's narrow rail picks it up.
  const [searchInput, setSearchInput] = useState<string>(urlSearch.search ?? '');
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Pull external URL changes (back / forward, deep-link, programmatic
  // navigation) back into the local input. Without this, the debounce-write
  // effect below would immediately overwrite the new URL with the stale
  // local input value, breaking back/forward for `?search=…` bookmarks.
  // Skip the sync while the user is mid-typing — once the debounce flushes,
  // input and URL agree again.
  useEffect(() => {
    const fromUrl = urlSearch.search ?? '';
    if (fromUrl !== searchInput && fromUrl !== debouncedSearch) {
      setSearchInput(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally
    // only depends on `urlSearch.search`; `searchInput` / `debouncedSearch`
    // are read but should not retrigger the sync (otherwise typing fights it).
  }, [urlSearch.search]);

  // Mirror filter state into the URL via `replace` so the detail page's
  // narrow rail matches and bookmarks / back-forward navigation work.
  // Only writes when the URL is stale to avoid render loops.
  useEffect(() => {
    const desired = debouncedSearch || undefined;
    if ((urlSearch.search ?? undefined) === desired) return;
    navigate({
      to: '/vozidla',
      search: { ...urlSearch, search: desired },
      replace: true,
    });
  }, [debouncedSearch, urlSearch, navigate]);

  function setStav(next: StavFilter) {
    navigate({
      to: '/vozidla',
      search: { ...urlSearch, stav: next === DEFAULT_STAV ? undefined : next },
      replace: true,
    });
  }
  function setBrandId(next: number | null) {
    navigate({
      to: '/vozidla',
      // Brand change resets the model — model is brand-scoped, so a stale
      // value here would filter the list against a model from a different brand.
      search: { ...urlSearch, brand: next ?? undefined, model: undefined },
      replace: true,
    });
  }
  function setModelId(next: number | null) {
    navigate({
      to: '/vozidla',
      search: { ...urlSearch, model: next ?? undefined },
      replace: true,
    });
  }
  function setDeliveryMonth(next: string | null) {
    navigate({
      to: '/vozidla',
      search: { ...urlSearch, deliveryMonth: next ?? undefined },
      replace: true,
    });
  }

  const onSubtabChange = (next: Subtab) => {
    if (next === 'upravena') {
      navigate({ to: '/vozidla/upravena' });
      return;
    }
    if (next === 'nabidky') {
      navigate({ to: '/vozidla/nabidky' });
      return;
    }
    if (next === 'smlouvy') {
      navigate({ to: '/vozidla/smlouvy' });
      return;
    }
    if (next === 'vyhodnoceni') {
      navigate({ to: '/vozidla/vyhodnoceni' });
      return;
    }
    if (next === 'sprava') {
      navigate({ to: '/vozidla/sprava' });
      return;
    }
    setSubtab(next);
  };

  const filters = useMemo(
    () =>
      vozidlaFiltersFromSearch({
        stav,
        brand: brandId ?? undefined,
        model: modelId ?? undefined,
        search: debouncedSearch,
        deliveryMonth: deliveryMonth ?? undefined,
      }),
    [brandId, modelId, stav, debouncedSearch, deliveryMonth],
  );

  const skupina = useRozsah('vozidla');
  const activeFirmaId = useActiveFirmaId();
  const listFilters = useMemo(
    () => (skupina.rozsah === 'skupina' ? { ...filters, rozsah: 'skupina' as const } : filters),
    [filters, skupina.rozsah],
  );
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useVehiclesInfiniteQuery(listFilters);
  const brands = useBrandsQuery();
  const models = useVehicleModelsQuery(brandId);

  const vehicles = useMemo(() => data?.pages.flatMap((p) => p.vehicles) ?? [], [data]);
  const total = data?.pages[0]?.total ?? 0;
  const selected = vehicles.find((v) => v.id_vozidla === selectedId) ?? vehicles[0] ?? null;
  const brandName = useMemo(() => {
    if (brandId == null) return null;
    return brands.data?.brands.find((b) => b.id === brandId)?.nazev ?? null;
  }, [brandId, brands.data]);
  const modelName = useMemo(() => {
    if (modelId == null) return null;
    return models.data?.models.find((m) => m.id === modelId)?.nazev_modelu ?? null;
  }, [modelId, models.data]);

  // The server counts the whole result; the loaded rows are only the pages fetched so far.
  const pocty = data?.pages[0]?.pocty;
  const counts = {
    total,
    registrovany: pocty?.registrovany ?? 0,
    skladovy: pocty?.skladovy ?? 0,
    vyplacene: pocty?.vyplacene ?? 0,
  };

  return (
    <Shell rail={<AppRail active="vozidla" />} inspector={<VehicleInspector vehicle={selected} activeFirmaId={activeFirmaId} />}>
      <Crown>
        <Breadcrumb segments={[{ label: 'Vozidla' }, { label: 'Přehled' }]} />
        <Crown.Row className="mt-3">
          <DisplayHeading>Vozidla</DisplayHeading>
          <CrownMeta
            items={[
              { label: 'Celkem', value: formatInt(counts.total) },
              { label: 'Registrovaná', value: formatInt(counts.registrovany) },
              { label: 'Skladová', value: formatInt(counts.skladovy) },
              { label: 'Vyplacená', value: formatInt(counts.vyplacene) },
            ]}
          />
        </Crown.Row>
        <Subtabs
          aria-label="Vozidla — sekce"
          value={subtab}
          onChange={onSubtabChange}
          items={[
            { value: 'vozidla', label: 'Vozidla', count: counts.total },
            { value: 'upravena', label: 'Upravená vozidla' },
            { value: 'nabidky', label: 'Nabídky' },
            { value: 'smlouvy', label: 'Smlouvy' },
            { value: 'vyhodnoceni', label: 'Vyhodnocení' },
            ...(canSprava ? [{ value: 'sprava' as const, label: 'Správa' }] : []),
          ]}
          trailing={
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden>→</span>
              <Kbd>⌘K</Kbd>
            </span>
          }
        />
      </Crown>

      <Toolband>
        <SearchInput
          placeholder="Č. obj., SPZ, VIN, model, umístění…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          wrapperClassName="max-w-[360px] flex-1"
        />
        <Menu>
          <Menu.Trigger asChild>
            <Chip icon={<Icon name="filter" size={12} />} active={stav !== 'all'}>
              {STAV_LABEL[stav]}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Filtr stavu</Menu.Label>
            {(Object.keys(STAV_LABEL) as StavFilter[]).map((key) => (
              <Menu.CheckItem
                key={key}
                checked={stav === key}
                onCheckedChange={(checked) => {
                  if (checked) setStav(key);
                }}
              >
                {STAV_LABEL[key]}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <RozsahChip skupina={skupina} />
        <Menu>
          <Menu.Trigger asChild>
            <Chip icon={<Icon name="filter" size={12} />} active={brandId != null}>
              {brandName ?? 'Značka'}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Filtr značky</Menu.Label>
            <Menu.Item onSelect={() => setBrandId(null)}>Všechny značky</Menu.Item>
            <Menu.Separator />
            {(brands.data?.brands ?? []).map((b) => (
              <Menu.CheckItem
                key={b.id}
                checked={brandId === b.id}
                onCheckedChange={(checked) => setBrandId(checked ? b.id : null)}
              >
                {b.nazev}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <Menu>
          <Menu.Trigger asChild>
            <Chip
              icon={<Icon name="filter" size={12} />}
              active={modelId != null}
              disabled={brandId == null}
            >
              {modelName ?? 'Model'}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Filtr modelu</Menu.Label>
            <Menu.Item onSelect={() => setModelId(null)}>Všechny modely</Menu.Item>
            <Menu.Separator />
            {(models.data?.models ?? []).map((m) => (
              <Menu.CheckItem
                key={m.id}
                checked={modelId === m.id}
                onCheckedChange={(checked) => setModelId(checked ? m.id : null)}
              >
                {m.nazev_modelu}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <Menu>
          <Menu.Trigger asChild>
            <Chip icon={<Icon name="filter" size={12} />} active={deliveryMonth != null}>
              {deliveryMonth ? deliveryMonthLabel(deliveryMonth) : 'Měsíc předání'}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Měsíc předání</Menu.Label>
            <Menu.Item onSelect={() => setDeliveryMonth(null)}>Všechny měsíce</Menu.Item>
            <Menu.Separator />
            {monthOptions.map((opt, i) => {
              const prevYear = i > 0 ? monthOptions[i - 1].year : null;
              return (
                <Fragment key={opt.value}>
                  {opt.year !== prevYear && <Menu.Label>{opt.year}</Menu.Label>}
                  <Menu.CheckItem
                    checked={deliveryMonth === opt.value}
                    onCheckedChange={(checked) => setDeliveryMonth(checked ? opt.value : null)}
                  >
                    {opt.label}
                  </Menu.CheckItem>
                </Fragment>
              );
            })}
          </Menu.Content>
        </Menu>
        <Toolband.Spacer />
        {canWrite && (
          <Button
            size="sm"
            variant="primary"
            icon={<Icon name="plus" size={12} />}
            onClick={() => navigate({ to: '/vozidla/novy' })}
          >
            Nové vozidlo
          </Button>
        )}
      </Toolband>

      <div data-testid="vozidla-page" className="flex min-h-0 flex-1 flex-col px-8 pb-6 pt-3 max-sm:px-0 max-sm:pb-4 max-sm:pt-1">
        <DataTable<Vehicle>
          aria-label="Seznam vozidel"
          variant="bordered"
          columns={vehicleColumns(stav, activeFirmaId)}
          data={isLoading ? [] : vehicles}
          getRowId={(v) => String(v.id_vozidla)}
          selectedId={selected ? String(selected.id_vozidla) : undefined}
          onRowClick={(v) =>
            // Phone (14.3): no inspector exists — tapping a card navigates to
            // the detail route, forwarding the list cohort for its BackBar.
            isPhone
              ? navigate({
                  to: '/vozidla/$id',
                  params: { id: String(v.id_vozidla) },
                  search: urlSearch,
                })
              : setSelectedId(v.id_vozidla)
          }
          getRowTint={(v): RowTint | undefined => {
            if (isRegistrovany(v)) return 'amber';
            if (isVyplaceny(v)) return 'mint';
            return undefined;
          }}
          empty={
            isLoading ? (
              <ListSkeleton />
            ) : isError ? (
              <EmptyState
                eyebrow="Chyba"
                title="Vozidla se nepodařilo načíst"
                description="Zkuste obnovit stránku. Pokud problém trvá, kontaktujte správce."
              />
            ) : (
              <EmptyState
                eyebrow="Prázdné"
                title="Žádná vozidla"
                description="Zkuste rozšířit filtry nebo vytvořte nové vozidlo."
              />
            )
          }
          className="min-h-0 flex-1"
        />
      </div>
      <LoadMoreBar
        loaded={vehicles.length}
        total={total}
        hasMore={!!hasNextPage}
        loading={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        unit="vozidel"
      />
    </Shell>
  );
}

function vehicleColumns(stav: StavFilter, activeFirmaId: number | null): DataTableColumn<Vehicle>[] {
  const widths = COLUMNS_TEMPLATE.split(' ');
  // Context-dependent columns (as in the legacy list): when filtering customer-owned vehicles the price column is replaced by the
  // customer, since price is irrelevant once a vehicle belongs to a customer.
  const showCustomer = stav === 'zakaznicky' || stav === 'predany_zakaznikovi';
  return [
    {
      id: 'flag',
      priority: 'meta',
      header: '',
      width: widths[0],
      align: 'center',
      cell: (v) => <ReservationFlag vehicle={v} />,
    },
    {
      id: 'cobj',
      priority: 'meta',
      header: 'Č. obj.',
      width: widths[1],
      cell: (v) => (
        <span className="font-mono text-[12px] font-semibold tracking-[-0.02em] text-ink">
          {v.vc_auta || '—'}
        </span>
      ),
    },
    {
      id: 'spz',
      priority: 'secondary',
      header: 'SPZ',
      width: widths[2],
      cell: (v) => (
        <span
          className={
            'font-mono text-[11px] tracking-[-0.02em] ' + (v.spz_vozu ? 'text-ink-2' : 'text-ink-3')
          }
        >
          {v.spz_vozu || '—'}
        </span>
      ),
    },
    {
      id: 'model',
      priority: 'primary',
      header: 'Model',
      width: widths[3],
      cell: (v) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-semibold tracking-[-0.01em] text-ink">
            {v.nazev_modelu ?? '—'}
          </span>
          <span className="truncate text-[11px] text-ink-3">{v.znacka_nazev ?? ''}</span>
        </div>
      ),
    },
    {
      id: 'prov',
      priority: 'secondary',
      header: 'Provedení / Barva / Motor',
      width: widths[4],
      cell: (v) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-[11.5px] text-ink-2">{v.provedeni || '—'}</span>
          <span className="flex items-center gap-1.5 truncate text-[10.5px] text-ink-3">
            {v.barva && <ColorSwatch color={vehicleColorToHex(v.barva)} size={8} />}
            <span className="truncate">
              {[v.barva, v.motor].filter(Boolean).join(' · ') || '—'}
            </span>
          </span>
        </div>
      ),
    },
    showCustomer
      ? {
          id: 'zakaznik',
          priority: 'meta',
          header: 'Zákazník',
          width: '200px',
          cell: (v) => {
            const name = v.jmeno_zakaznika?.trim();
            if (!name) return <span className="text-ink-3">—</span>;
            const id = Number(v.id_zakaznika);
            if (!Number.isFinite(id) || id <= 0) {
              return <span className="truncate text-[12px] text-ink-2">{name}</span>;
            }
            return (
              <Link
                to="/zakaznici/$id"
                params={{ id: String(id) }}
                className="truncate text-[12px] font-medium text-ink underline-offset-2 hover:underline"
              >
                {name}
              </Link>
            );
          },
        }
      : {
          id: 'cena',
          priority: 'meta',
          header: 'Cena',
          width: widths[5],
          align: 'right',
          cell: (v) => {
            const price = Number(v.prodejni_cena_navrh || 0);
            const has = price > 0;
            return (
              <span
                className={
                  'font-mono text-[11.5px] tracking-[-0.02em] ' +
                  (has ? 'font-semibold text-ink' : 'font-normal text-ink-3')
                }
              >
                {has ? `${formatInt(price)} Kč` : '—'}
              </span>
            );
          },
        },
    {
      id: 'umisteni',
      header: 'Umístění',
      width: widths[6],
      cell: (v) => <span className="truncate text-[11px] text-ink-2">{v.umisteni || '—'}</span>,
    },
    {
      id: 'baterie',
      header: 'Bat.',
      width: widths[7],
      align: 'center',
      cell: (v) => <BatteryDot level={batteryLevel(v)} />,
    },
    {
      id: 'stav',
      priority: 'meta',
      header: 'Stav',
      width: widths[8],
      cell: (v) => (
        <span
          className="flex min-w-0 flex-col items-start gap-0.5"
          title={v.preveden_do_firma ? `Převedeno do firmy ${v.preveden_do_firma}` : undefined}
        >
          <Badge size="sm" tone={stavBadgeTone(v)}>{stavBadgeLabel(v.stav_vozidla)}</Badge>
          {otherFirmaName(v, activeFirmaId) && v.firma_nazev && (
            <Badge tone="accent" size="sm" className="max-w-full">
              <span className="truncate" title={`Vozidlo firmy ${v.firma_nazev}`}>
                {v.firma_nazev}
              </span>
            </Badge>
          )}
        </span>
      ),
    },
  ];
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-px p-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function VehicleInspector({ vehicle, activeFirmaId }: { vehicle: Vehicle | null; activeFirmaId: number | null }) {
  // All hooks must run unconditionally — keep them above any early return.
  const urlSearch = useSearch({ from: '/vozidla' });
  const navigate = useNavigate();
  // Phase 12.2 calibration: a single Inspector element with `selectionKey`
  // pegged to the currently-selected vehicle id triggers the framer-motion
  // crossfade between selections. Empty + populated branches share the
  // wrapper so AnimatePresence sees them as the same instance.
  if (!vehicle) {
    return (
      <Inspector selectionKey={null}>
        <div className="grid h-full place-items-center px-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Vyberte vozidlo
        </div>
      </Inspector>
    );
  }

  const price = Number(vehicle.prodejni_cena_navrh || 0);
  const isReg = isRegistrovany(vehicle);
  // A car of another firma of the group (combined list): its detail opens read-only, in its own firma.
  const otherFirma = otherFirmaName(vehicle, activeFirmaId);

  return (
    <Inspector selectionKey={vehicle.id_vozidla} className="vt-detail-card">
      <Inspector.Head
        eyebrow={
          <>
            — {vehicle.vc_auta || '—'} · {isReg ? 'Registrované' : 'Skladové'}
          </>
        }
        title={
          <>
            {vehicleDisplayName(vehicle) || vehicle.nazev_modelu || 'Vozidlo'}
            {vehicle.provedeni && (
              <span className="font-medium italic text-ink-2"> {vehicle.provedeni}.</span>
            )}
          </>
        }
        sub={
          <span className="inline-flex items-center gap-2.5">
            <Pill>{vehicle.spz_vozu || 'BEZ SPZ'}</Pill>
            {vehicle.barva && (
              <span className="inline-flex items-center gap-1.5">
                <ColorSwatch color={vehicleColorToHex(vehicle.barva)} size={10} />
                <span className="font-sans text-[12px] text-ink-2">{vehicle.barva}</span>
              </span>
            )}
          </span>
        }
      />
      <Inspector.Body>
        {otherFirma && (
          <Inspector.Section heading="— Firma">
            <FieldRow k="Vozidlo firmy" v={otherFirma} />
          </Inspector.Section>
        )}
        <Inspector.Section heading="— Cena">
          {price > 0 ? (
            <div className="flex items-baseline gap-1.5 font-sans text-[28px] font-bold tracking-[-0.02em] text-ink">
              {formatInt(price)}
              <small className="text-[14px] font-medium text-ink-3">Kč</small>
            </div>
          ) : (
            <div className="font-sans text-[20px] font-medium text-ink-3">—</div>
          )}
        </Inspector.Section>
        <Inspector.Section heading="— Specifikace">
          <FieldRow k="Značka" v={vehicle.znacka_nazev ?? '—'} />
          <FieldRow
            k="Baterie"
            v={
              <span className="inline-flex items-center gap-2">
                <BatteryDot level={batteryLevel(vehicle)} />
                {batteryLabel(vehicle)}
              </span>
            }
          />
          <FieldRow k="Motor" v={vehicle.motor || '—'} />
          <FieldRow k="Převodovka" v={vehicle.prevodovka || '—'} />
          <FieldRow k="Modelový rok" v={vehicle.modelovy_rok || '—'} mono />
          <FieldRow k="Umístění" v={vehicle.umisteni || '—'} />
          {vehicle.cislo_zakazky && <FieldRow k="Č. zakázky" v={vehicle.cislo_zakazky} mono />}
        </Inspector.Section>
        {vehicle.datum_predani && (
          <Inspector.Section heading="— Termíny">
            <FieldRow k="Předání" v={formatDate(vehicle.datum_predani)} mono />
          </Inspector.Section>
        )}
        {(vehicle.cislo_faktury ||
          vehicle.datum_faktury ||
          Number(vehicle.hodnota_faktury) > 0) && (
          <Inspector.Section heading="— Faktura">
            {!!vehicle.cislo_faktury && (
              <FieldRow k="Č. faktury" v={String(vehicle.cislo_faktury)} mono />
            )}
            {vehicle.datum_faktury && (
              <FieldRow k="Datum" v={formatDate(vehicle.datum_faktury)} mono />
            )}
            {Number(vehicle.hodnota_faktury) > 0 && (
              <FieldRow
                k="Hodnota"
                v={`${formatDecimal(Number(vehicle.hodnota_faktury) * 1.21)} Kč vč. DPH`}
                mono
              />
            )}
          </Inspector.Section>
        )}
        {(vehicle.jmeno_zakaznika || vehicle.rezervace) && (
          <Inspector.Section heading="— Rezervace">
            {vehicle.jmeno_zakaznika && <FieldRow k="Zákazník" v={vehicle.jmeno_zakaznika} />}
            {vehicle.rezervace && <FieldRow k="Rezervace" v={vehicle.rezervace} />}
          </Inspector.Section>
        )}
        {vehicle.vin && (
          <Inspector.Section heading="— VIN">
            <code className="block select-all break-all border border-line bg-bg p-2 font-mono text-[11px] tracking-[-0.02em] text-ink-2">
              {vehicle.vin}
            </code>
          </Inspector.Section>
        )}
        {vehicle.poznamka && (
          <Inspector.Section heading="— Poznámka">
            <p className="whitespace-pre-line text-[12.5px] text-ink-2">{vehicle.poznamka}</p>
          </Inspector.Section>
        )}
      </Inspector.Body>
      <Inspector.Foot>
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="external" size={12} />}
          onClick={() =>
            navigate({
              to: '/vozidla/$id',
              params: { id: String(vehicle.id_vozidla) },
              search: urlSearch,
            })
          }
        >
          Detail
        </Button>
      </Inspector.Foot>
    </Inspector>
  );
}

function batteryLabel(v: Vehicle): string {
  const level = batteryLevel(v);
  if (level === 'high') return 'Plná';
  if (level === 'medium') return 'Střední';
  if (level === 'low') return 'Nízká';
  return 'Neznámá';
}
